import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const POINTS = [
    "Targeted email campaigns built from real customer segments",
    "Automated customer journeys — welcome, service reminders, win-back",
    "Follow-ups triggered by enquiries, quotes and purchase history",
    "Reviews and customer communications kept on the same record",
];

const CampaignPanel = () => (
    <div className="relative flex h-full flex-col rounded-2xl border border-[#0F172A]/[0.07] bg-white p-5 shadow-[0_24px_60px_-24px_rgba(11,18,32,0.25)]" data-testid="marketing-panel">
        <span className="absolute right-3 top-3 rounded-full border border-[#0F172A]/10 bg-white/90 px-2 py-0.5 font-mono-brand text-[8px] uppercase tracking-[0.2em] text-[#94A3B8]">
            Concept visual — sample data
        </span>
        <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#0F172A]">Campaigns</p>
            <span className="rounded-full bg-[#0066FF]/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#0066FF]">
                Automation on
            </span>
        </div>
        <div className="mt-4 rounded-xl border border-[#0F172A]/[0.06] bg-[#F8FAFF] p-4">
            <div className="flex items-center gap-2">
                <span className="rounded-full bg-[#A855F7]/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#A855F7]">
                    Win-back journey
                </span>
                <span className="text-[10px] font-semibold text-[#64748B]">Running automatically</span>
            </div>
            <p className="mt-3 text-xs font-bold text-[#0F172A]">Segment · Due for annual service — 214 customers</p>
            <div className="mt-2.5 rounded-lg border border-[#0F172A]/[0.06] bg-white px-3 py-2.5">
                <p className="text-[9px] uppercase tracking-wider text-[#94A3B8]">Email subject</p>
                <p className="mt-0.5 text-[11px] font-semibold text-[#334155]">Time for your annual service, {'{first name}'}?</p>
            </div>
            <div className="mt-3 flex items-center gap-1.5">
                {["Segment built", "Email sent", "Reminder queued"].map((s, i) => (
                    <span key={s} className="flex items-center gap-1.5">
                        <span className={`flex items-center gap-1 rounded-full px-2 py-1 text-[8px] font-bold uppercase tracking-wider ${i < 2 ? "bg-[#0066FF]/10 text-[#0066FF]" : "bg-[#0F172A]/[0.05] text-[#64748B]"}`}>
                            {i < 2 && <CheckCircle2 className="h-2.5 w-2.5" />}
                            {s}
                        </span>
                        {i < 2 && <span className="h-px w-2 bg-[#0F172A]/15" />}
                    </span>
                ))}
            </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
            {[
                ["Delivered", "1,240"],
                ["Opened", "61%"],
                ["Rebooked", "38"],
            ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-[#0F172A]/[0.05] bg-[#F8FAFF] px-2 py-2.5 text-center">
                    <p className="font-display text-sm font-extrabold text-[#0F172A]">{value}</p>
                    <p className="mt-0.5 text-[8px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
                </div>
            ))}
        </div>
        <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            className="mt-4 flex items-center gap-2.5 rounded-xl border border-[#0066FF]/15 bg-gradient-to-r from-[#F0F9FF] to-[#FAF5FF] p-3"
        >
            <Sparkles className="h-4 w-4 shrink-0 text-[#A855F7]" />
            <p className="text-[11px] text-[#334155]">
                <span className="font-bold">AI suggestion:</span> 86 customers opened but didn't book — schedule the follow-up for Tuesday 9am.
            </p>
        </motion.div>
    </div>
);

export default function HomeMarketing() {
    return (
        <section
            className="relative overflow-hidden bg-[#F6F9FE]"
            style={{
                backgroundImage:
                    "radial-gradient(circle at 95% 0%, rgba(168,85,247,0.06) 0%, transparent 40%), radial-gradient(circle at 0% 100%, rgba(0,229,255,0.06) 0%, transparent 40%)",
            }}
            data-testid="home-marketing-section"
            aria-labelledby="home-marketing-heading"
        >
            <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 py-24 sm:py-32 lg:grid-cols-2 lg:gap-16">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    <span
                        className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm"
                        data-testid="home-marketing-eyebrow"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#A855F7]" />
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                            Marketing Automation
                        </span>
                    </span>
                    <h2
                        id="home-marketing-heading"
                        className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0F172A] sm:text-5xl"
                        data-testid="home-marketing-heading"
                    >
                        Turn Customer Data Into{" "}
                        <span className="text-brand-gradient">Smarter Marketing.</span>
                    </h2>
                    <p className="mt-6 max-w-lg text-base leading-relaxed text-[#64748B] sm:text-lg" data-testid="home-marketing-intro">
                        Your customer records, purchase history, enquiries and sales activity are
                        already in TedOps. Marketing automation puts them to work — targeted
                        campaigns, automated journeys and more relevant communications, without a
                        separate marketing tool.
                    </p>
                    <ul className="mt-8 space-y-3">
                        {POINTS.map((p) => (
                            <li key={p} className="flex items-start gap-3 text-[15px] font-medium text-[#334155]">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0066FF]" />
                                {p}
                            </li>
                        ))}
                    </ul>
                    <a
                        href="mailto:enquires@tedopsai.com?subject=Book%20a%20TedOps%20AI%20demo"
                        className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                        data-testid="home-marketing-cta"
                    >
                        See Marketing in Action
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                >
                    <CampaignPanel />
                </motion.div>
            </div>
        </section>
    );
}
